import React from 'react';
import { Button } from '@/components/ui/button';
import { ChevronLeft } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import AppHeader from '@/components/layout/AppHeader';
import { TopPageAd, BelowHeaderAd } from '@/components/ads/HeaderAds';
import { useTenant } from '@/contexts/TenantContext';
import { useDefaultHeaderStyle } from '@/hooks/useDefaultHeaderStyle';
import howToPlaySvg from '/howtoplay.svg';

interface HowToPlayScreenProps {
  onBack: () => void;
  onGotIt: () => void;
  className?: string;
}

const HowToPlayScreen: React.FC<HowToPlayScreenProps> = ({ onBack, onGotIt, className = '' }) => {
  const { t } = useTranslation();
  // Like PickEmApp, this is a landing-style screen with no single pickem in
  // context, so the header banner is gated on the tenant's default style config
  // (DKPLAT-437).
  const defaultHeaderStyle = useDefaultHeaderStyle();
  const { pickemFullNavigation } = useTenant();

  const steps = [
    { number: 1, title: t('howToPlay.step1Title'), description: t('howToPlay.step1Description') },
    { number: 2, title: t('howToPlay.step2Title'), description: t('howToPlay.step2Description') },
    {
      number: 3,
      title: t('howToPlay.step3Title'),
      description: t('howToPlay.step3Description'),
    },
    { number: 4, title: t('howToPlay.step4Title'), description: t('howToPlay.step4Description') },
  ];

  return (
    <div
      data-test-id="how-to-play-screen"
      className={`fixed inset-0 bg-app-background flex flex-col ${className}`}
    >
      {/* Back Button */}
      <div className="flex-shrink-0 px-4 pt-4 flex items-center gap-3">
        <button
          data-test-id="back-btn"
          onClick={onBack}
          className="text-text-secondary hover:text-text-primary transition-colors"
        >
          <ChevronLeft className="w-6 h-6" />
        </button>
        <span className="text-text-secondary text-lg">{t('howToPlay.back')}</span>
      </div>

      {/* Top-of-page ad placement */}
      <div className="flex-shrink-0 px-4">
        <TopPageAd />
      </div>

      {/* App Header — hidden when banner styling is disabled or empty (DKPLAT-437) */}
      {defaultHeaderStyle.isHeaderVisible && (
        <>
          <AppHeader
            className="!mt-[11px] !mb-0"
            headerLogoUrl={defaultHeaderStyle.headerLogoUrl}
            isLogoLoading={defaultHeaderStyle.isLoading}
            fullNavigation={pickemFullNavigation}
            suppressFallbacks
          />

          {/* Below-header ad placement */}
          <div className="flex-shrink-0 px-4">
            <BelowHeaderAd />
          </div>
        </>
      )}

      {/* Scrollable Content */}
      <div className="flex-1 overflow-y-auto px-6 pb-6 min-h-0">
        <div className="max-w-md mx-auto space-y-4 sm:space-y-8">
          {/* Title */}
          <h1
            data-test-id="title"
            className="text-text-primary font-bold text-lg sm:text-2xl text-center pt-2 sm:pt-4"
          >
            {t('howToPlay.title')}
          </h1>

          {/* Logo */}
          <div className="flex justify-center">
            <div className="w-4/5 sm:w-full flex items-center justify-center">
              <img
                data-test-id="how-to-play-logo"
                src={howToPlaySvg}
                alt="How to Play"
                className="max-w-full h-auto object-contain"
              />
            </div>
          </div>

          {/* Steps */}
          <div
            data-test-id="steps-section"
            className="space-y-3 sm:space-y-6 pt-2 sm:pt-4 flex flex-col items-center"
          >
            {steps.map(step => (
              <div key={step.number} data-test-id={`step-${step.number}`} className="text-center">
                <div className="flex items-center justify-center gap-2 sm:gap-3 mb-1 sm:mb-2">
                  <div className="w-7 h-7 sm:w-10 sm:h-10 bg-pickem-primary rounded-full flex items-center justify-center flex-shrink-0">
                    <span className="text-white font-bold text-sm sm:text-lg">{step.number}</span>
                  </div>
                  <h3 className="text-text-primary font-bold text-sm sm:text-lg">{step.title}</h3>
                </div>
                <p className="text-text-secondary text-xs sm:text-sm">{step.description}</p>
              </div>
            ))}
          </div>

          {/* Action Button */}
          <div className="pt-3 sm:pt-6 pb-4">
            <Button
              data-test-id="got-it-btn"
              variant="pickem"
              onClick={onGotIt}
              className="w-full h-11 sm:h-14 text-sm sm:text-lg font-semibold"
            >
              {t('howToPlay.cta')}
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default HowToPlayScreen;
